/**
 * Namespaced UI dismissal flags — "this member has dismissed X" and nothing more.
 *
 * `localStorage` rather than `sessionStorage`: a dismissal is a decision the member made, so it
 * should outlive the tab it was made in and survive a browser relaunch. The cost is that nothing
 * expires it on its own, which makes the logout clear below load-bearing rather than tidy-up --
 * without it the next member to log in on this browser inherits the last one's dismissals.
 *
 * Only keys under `PREFIX` are touched. Clearing all of `localStorage` would take the auth tokens
 * and whatever the third-party SDKs sharing this origin keep there.
 *
 * Not for anything sensitive: this is readable by any script on the origin and is never cleared
 * except on logout.
 */
const PREFIX = 'collective:';

const namespaced = (key: string) => `${PREFIX}${key}`;

/**
 * Every accessor swallows its errors: `localStorage` throws in some privacy modes and when the
 * quota is exhausted, and a UI nicety is not worth a crash. Failing this way costs at most a flag
 * that lasts one page load.
 */
export function getDismissalFlag(key: string): boolean {
    try {
        return window.localStorage.getItem(namespaced(key)) === 'true';
    } catch {
        return false;
    }
}

export function setDismissalFlag(key: string): void {
    try {
        window.localStorage.setItem(namespaced(key), 'true');
    } catch {
        // Flag then lasts only as long as this page, which is the safe way to fail.
    }
}

/**
 * Drops every flag this module owns. Called on logout: these outlive the tab, so without this one
 * member's dismissals would follow the next login on the same browser.
 */
export function clearDismissalFlags(): void {
    try {
        Object.keys(window.localStorage)
            .filter((key) => key.startsWith(PREFIX))
            .forEach((key) => window.localStorage.removeItem(key));
    } catch {
        // Nothing to clear if storage is unavailable.
    }
}
